/**
 * The employee's answer to a manager's correction of their time (piece 34,
 * *Time capture*; decision 12930; T3855). 🔒 A correction is a new entry that
 * names the entry it corrects (decision 1906); the employee the hours belong
 * to answers it Agree or Dispute, once, and the answer is kept on the
 * correcting entry. 🔨 The answer never changes the hours, and a dispute
 * raises a To-Do on the approver who approved the correction.
 */

import { t } from '../strings';

/** 🔒 The two answers, as the `TimeCorrectionAnswer` enum stores them. */
export const TIME_CORRECTION_ANSWERS = ['Agreed', 'Disputed'] as const;
export type TimeCorrectionAnswerKind = (typeof TIME_CORRECTION_ANSWERS)[number];

/** The To-Do a dispute raises on the approver, closed by a tick (piece 9's kinds). */
export const TIME_CORRECTION_DISPUTED_KIND = 'TimeCorrectionDisputed';
/** The record the To-Do names: the employment whose time was corrected. */
export const TIME_CORRECTION_DISPUTED_SOURCE = 'EmploymentRecord';

/** The longest reason a dispute keeps, a sentence or two. */
export const TIME_CORRECTION_REASON_MAXIMUM = 500;

/** The employee's answer to one correction, relative to the API root. */
export function timeCorrectionAnswerPath(entryId: string): string {
  return `/hr/my-info/time-corrections/${encodeURIComponent(entryId)}/answer`;
}

/** What the employee sends: the answer, and the reason where it is a dispute. */
export interface TimeCorrectionAnswerDraft {
  answer: string;
  reason?: string | null;
}

/** One correction as My Info's Time section draws it, hours before and after, with the answer where one is kept. */
export interface TimeCorrectionRow {
  /** The correcting entry's id, which the answer is written against. */
  id: string;
  workDate: string;
  workOrderNumber: string | null;
  /** The hours before this correction and after it; the change is their difference. */
  hoursBefore: number;
  hoursAfter: number;
  correctedByName: string;
  reason: string;
  answer: TimeCorrectionAnswerKind | null;
  answeredAt: string | null;
  disputeReason: string | null;
  /** The viewer is the employee and the correction is approved and unanswered, so the two buttons draw. */
  mayAnswer: boolean;
}

/** The Time section's corrections, sent to the employee and to the managers above the job (read-only). */
export interface TimeCorrectionsSection {
  rows: TimeCorrectionRow[];
}

/** Refusals, each a sentence. */
export const TIME_CORRECTION_REFUSALS = {
  notFound: 'No correction to your time was found with that id.',
  notYours: 'Only the employee whose time was corrected answers the correction.',
  ownCorrection: 'A correction you made on your own time asks no answer.',
  notApproved: 'A correction is answered once it is approved, and this one is not.',
  answered: 'This correction has been answered already, and an answer is kept as it was given.',
} as const;

/** 🔨 Whether a correction asks the employee an answer: another person made it, it is approved, and none is kept. */
export function timeCorrectionAsksAnswer(entry: { isCorrection: boolean; madeByEmployee: boolean; approvalStatus: string; answered: boolean }): boolean {
  return entry.isCorrection && !entry.madeByEmployee && entry.approvalStatus === 'Approved' && !entry.answered;
}

/** The first sentence that refuses an answer to a correction, or null. Ownership is the caller's check, made before it. */
export function timeCorrectionRefusal(entry: { isCorrection: boolean; madeByEmployee: boolean; approvalStatus: string; answered: boolean }): string | null {
  if (!entry.isCorrection) return TIME_CORRECTION_REFUSALS.notFound;
  if (entry.answered) return TIME_CORRECTION_REFUSALS.answered;
  if (entry.madeByEmployee) return TIME_CORRECTION_REFUSALS.ownCorrection;
  if (entry.approvalStatus !== 'Approved') return TIME_CORRECTION_REFUSALS.notApproved;
  return null;
}

/** The problems with an answer as typed: a word from the two, and a reason on a dispute alone, within the limit. */
export function timeCorrectionAnswerProblems(draft: TimeCorrectionAnswerDraft): string[] {
  const problems: string[] = [];
  const answer = draft.answer as TimeCorrectionAnswerKind;
  if (!TIME_CORRECTION_ANSWERS.includes(answer)) return ['Answer Agree or Dispute.'];
  const reason = (draft.reason ?? '').trim();
  if (answer === 'Disputed' && reason === '') problems.push('A dispute says why, in your own words.');
  if (answer === 'Agreed' && reason !== '') problems.push('An agreement carries no reason.');
  if (reason.length > TIME_CORRECTION_REASON_MAXIMUM) problems.push(`A reason is at most ${TIME_CORRECTION_REASON_MAXIMUM} characters.`);
  return problems;
}

/** A change in hours as a person reads it: *0.5 hours fewer*, *1.25 hours more*. */
export function correctionChangeText(before: number, after: number): string {
  const change = Math.round((after - before) * 100) / 100;
  const size = Math.abs(change);
  return `${size} ${t(change < 0 ? (size === 1 ? 'hr.timeCorrection.hourFewer' : 'hr.timeCorrection.fewer') : size === 1 ? 'hr.timeCorrection.hourMore' : 'hr.timeCorrection.more')}`;
}
